import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CollectionsIcon from "@mui/icons-material/Collections";
import { useSelector, useDispatch } from "react-redux";
import { rowsPerPage } from "../../../Constants";
import Attachments from "./Utils/Attachments";
import GalleryUpload from "./Utils/GalleryUpload";
import { buildMedia } from "../../Utils/AuthService";
import {
  StyledButton,
  StyledContainer,
  StyledTableCell,
} from "../../CSS/Styles";
import {
  fetchSlideshow,
  saveSlideshow,
  getSlideItem,
  deleteSlideItem,
  orderSlideshowItems,
} from "../../../redux/slices/orgAdmin/slideshowSlice";
import Banner from "../../Utils/Snackbar";

export default function Slideshow() {
  const dispatch = useDispatch();
  const { slideshowItems, loading } = useSelector((state) => state.slideshow);
  const { companyId } = useSelector((state) => state.app);

  const [deleteTitle, setDeleteTitle] = useState("");
  const [media, setMedia] = useState(null);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [galleryId, setGalleryId] = useState(null);

  const [slideshowData, setSlideshowData] = useState({
    name: "",
    url: "",
    buttonLabel: "",
    attachments: {
      desktopImage: [],
      mobileImage: [],
    },
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemIdToDelete, setItemIdToDelete] = useState(null);
  const [localItems, setLocalItems] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [page, setPage] = useState(1);
  const totalRecords =
    slideshowItems?.pagination?.totalRecords || slideshowItems?.length || 0;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const files = {};
  const sectionId = 10; // Slideshow section ID
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [rowToEdit, setRowToEdit] = useState(null);

  useEffect(() => {
    dispatch(fetchSlideshow(companyId));
  }, [dispatch, companyId]);

  useEffect(() => {
    if (slideshowItems?.length) {
      const parsedData = slideshowItems.reduce((acc, item) => {
        const content = JSON.parse(item?.content || "{}");
        acc.push({
          id: item.id,
          name: content?.name || "",
          url: content?.url || "",
          buttonLabel: content?.buttonLabel || "",
          desktopImage: item.desktopImage || "",
          mobileImage: item.mobileImage || "",
        });
        return acc;
      }, []);
      setLocalItems(parsedData);
    } else {
      setLocalItems([]);
    }
  }, [slideshowItems]);

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(localItems);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    setLocalItems(reorderedItems);

    const reorderedData = reorderedItems?.map((item, index) => ({
      id: item.id,
      sortIndex: index + 1,
    }));

    dispatch(orderSlideshowItems(reorderedData))
      .unwrap()
      .then(() => {
        dispatch(fetchSlideshow(companyId));
      })
      .catch((error) => {
        console.error("Error updating order:", error);
      });
  };

  const handleInputChange = (field, value) => {
    setSlideshowData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditItem = async (id) => {
    try {
      setRowToEdit(id);
      const actualId = id && typeof id === "object" ? id.id : id;

      const itemData = await dispatch(getSlideItem({ id: actualId })).then(
        (res) => res.payload
      );

      if (
        itemData?.status === "error" &&
        itemData.message === "Record not found"
      ) {
        alert("Record not found.");
        return;
      }
      if (!itemData || !itemData.data) return;

      const slideId = itemData.data?.id;
      const desktopImage = itemData.data?.desktopImage || "";
      const mobileImage = itemData.data?.mobileImage || "";

      // Build media data structure for both desktop and mobile images
      const mediaData = {
        desktopImage: desktopImage
          ? `${process.env.REACT_APP_API_URL}/uploads/${companyId}/${desktopImage}`
          : null,
        mobileImage: mobileImage
          ? `${process.env.REACT_APP_API_URL}/uploads/${companyId}/${mobileImage}`
          : null,
      };

      setMedia(mediaData);

      const prefilledData = {
        name: itemData.data?.name || "",
        url: itemData.data?.url || "",
        buttonLabel: itemData.data?.buttonLabel || "",
        attachments: {
          desktopImage: [],
          mobileImage: [],
        },
        deleteDesktopImage: false,
        deleteMobileImage: false,
        deletedAttachments: [],
      };

      setSlideshowData(prefilledData);
      setIsAddingItem(true);
    } catch (error) {
      console.error("Error fetching slideshow item data:", error);
    }
  };

  const handleAddItem = () => {
    setRowToEdit(null);
    setMedia(null);
    setSlideshowData({
      name: "",
      url: "",
      buttonLabel: "",
      attachments: {
        desktopImage: [],
        mobileImage: [],
      },
    });
    setIsAddingItem(true);
  };

  const handleAttachmentsChange = (newAttachments, imageType) => {
    setSlideshowData((prevData) => {
      const prevAttachments = prevData.attachments || {};

      return {
        ...prevData,
        attachments: {
          ...prevAttachments,
          [imageType]: newAttachments.mainImage || null,
        },
      };
    });
  };

  const handleSaveItem = () => {
    // Basic validation
    if (!slideshowData.name.trim()) {
      setSnackbarMessage("Please provide a name for the slideshow item");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const payload = rowToEdit
      ? { id: typeof rowToEdit === "object" ? rowToEdit.id : rowToEdit }
      : {};

    // Add the basic fields to the payload
    payload.name = slideshowData.name.trim();
    payload.url = slideshowData.url.trim();
    payload.buttonLabel = slideshowData.buttonLabel.trim();

    // Attach files
    if (slideshowData.attachments?.desktopImage?.length) {
      files["desktopImage"] = slideshowData.attachments.desktopImage[0].file;
    }

    if (slideshowData.attachments?.mobileImage?.length) {
      files["mobileImage"] = slideshowData.attachments.mobileImage[0].file;
    }

    // Handle image deletion flags
    if (rowToEdit && slideshowData.deleteDesktopImage) {
      payload.deleteDesktopImage = true;
    }

    if (rowToEdit && slideshowData.deleteMobileImage) {
      payload.deleteMobileImage = true;
    }

    // Prepare the slideshow data structure exactly like SEO
    const slideshowPayload = {
      data: {
        en: {
          name: payload.name,
          url: payload.url,
          buttonLabel: payload.buttonLabel,
          desktopImage: files["desktopImage"] ? "uploaded" : "",
          mobileImage: files["mobileImage"] ? "uploaded" : "",
        },
      },
    };

    dispatch(saveSlideshow({ allSlideshowData: slideshowPayload, sectionId }))
      .unwrap()
      .then(() => {
        dispatch(fetchSlideshow(companyId));
        setSnackbarMessage("Slideshow item saved successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setSlideshowData({
          name: "",
          url: "",
          buttonLabel: "",
          attachments: {},
        });
        setRowToEdit(null);
        setIsAddingItem(false);
      })
      .catch((error) => {
        console.error("Error saving slideshow item:", error);
        setSnackbarMessage("Error saving slideshow item");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  const handleDeleteClick = (id, name) => {
    setItemIdToDelete(id);
    setDeleteDialogOpen(true);
    setDeleteTitle(name);
  };

  const handleShowGallery = (id, title) => {
    setGalleryId(id);
    setGalleryDialogOpen(true);
    setDeleteTitle(title);
  };

  const confirmDelete = () => {
    dispatch(deleteSlideItem({ id: itemIdToDelete, sectionId }))
      .then(() => {
        setDeleteDialogOpen(false);
        setItemIdToDelete(null);

        const updatedTotalRecords = totalRecords - 1;
        const updatedTotalPages = Math.ceil(updatedTotalRecords / rowsPerPage);

        const newPage = page > updatedTotalPages ? updatedTotalPages : page;

        setPage(newPage);
        setSnackbarMessage("Item deleted successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);

        dispatch(fetchSlideshow(companyId));
      })
      .catch((error) => {
        console.error("Error deleting slideshow item:", error);
        setSnackbarMessage("Error deleting slideshow item");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  return (
    <StyledContainer>
      <Paper elevation={3} sx={{ padding: 3 }}>
        {!isAddingItem && (
          <>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Slideshow Management
            </Typography>
            <StyledButton
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              sx={{ marginBottom: 2 }}
              disabled={loading}
            >
              Add Slideshow Item
            </StyledButton>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <TextField
                type="number"
                label="Edit by ID"
                variant="outlined"
                value={rowToEdit || ""}
                onChange={(e) => setRowToEdit(e.target.value)}
                size="small"
                sx={{ marginRight: 1 }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleEditItem(parseInt(rowToEdit))}
                disabled={loading || !rowToEdit}
              >
                Edit
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="slideshowTable">
                  {(provided) => (
                    <Table ref={provided.innerRef} {...provided.droppableProps}>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell width="5%"></StyledTableCell>
                          <StyledTableCell width="25%">Name</StyledTableCell>
                          <StyledTableCell width="30%">URL</StyledTableCell>
                          <StyledTableCell width="20%">
                            Button Label
                          </StyledTableCell>
                          <StyledTableCell width="20%">Actions</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(localItems || []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No slideshow items found. Click "Add Slideshow
                              Item" to create one.
                            </TableCell>
                          </TableRow>
                        ) : (
                          (localItems || []).map((item, index) => (
                            <Draggable
                              key={item.id || index}
                              draggableId={(item.id || index).toString()}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <TableRow
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  style={{
                                    ...provided.draggableProps.style,
                                    backgroundColor: snapshot.isDragging
                                      ? "#f5f5f5"
                                      : "inherit",
                                  }}
                                >
                                  {/* Drag Handle */}
                                  <TableCell {...provided.dragHandleProps}>
                                    <DragIndicatorIcon />
                                  </TableCell>

                                  {/* Name */}
                                  <TableCell>{item.name || "N/A"}</TableCell>

                                  {/* URL */}
                                  <TableCell>
                                    {item.url ? (
                                      <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {item.url.length > 30
                                          ? `${item.url.substring(0, 30)}...`
                                          : item.url}
                                      </a>
                                    ) : (
                                      "N/A"
                                    )}
                                  </TableCell>

                                  {/* Button Label */}
                                  <TableCell>
                                    {item.buttonLabel || "N/A"}
                                  </TableCell>

                                  {/* Actions */}
                                  <TableCell>
                                    <IconButton
                                      onClick={() => handleEditItem(item.id)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      onClick={() =>
                                        handleDeleteClick(item.id, item.name)
                                      }
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                    <IconButton
                                      onClick={() =>
                                        handleShowGallery(item.id, item.name)
                                      }
                                    >
                                      <CollectionsIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Draggable>
                          ))
                        )}
                        {provided.placeholder}
                      </TableBody>
                    </Table>
                  )}
                </Droppable>
              </DragDropContext>
            </TableContainer>
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          </>
        )}

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete{" "}
              <span style={{ fontWeight: "bold" }}> {deleteTitle}</span>? This
              action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <GalleryUpload
          galleryDialogOpen={galleryDialogOpen}
          setGalleryDialogOpen={setGalleryDialogOpen}
          deleteTitle={deleteTitle}
          fk_sectionDataId={galleryId}
          companyId={companyId}
        />

        {isAddingItem && (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <IconButton onClick={() => setIsAddingItem(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">
                {rowToEdit ? "Edit Slideshow Item" : "Add Slideshow Item"}
              </Typography>
            </Box>

            <TextField
              sx={{ mt: 2 }}
              label="Name"
              value={slideshowData.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              fullWidth
              inputProps={{ maxLength: 255 }}
              helperText={`${(slideshowData.name || "").length}/255`}
            />

            <TextField
              sx={{ mt: 2 }}
              label="URL"
              value={slideshowData.url || ""}
              onChange={(e) => handleInputChange("url", e.target.value)}
              fullWidth
              inputProps={{ maxLength: 255 }}
              helperText={`${(slideshowData.url || "").length}/255`}
            />

            <TextField
              sx={{ mt: 2 }}
              label="Button Label"
              value={slideshowData.buttonLabel || ""}
              onChange={(e) => handleInputChange("buttonLabel", e.target.value)}
              fullWidth
              inputProps={{ maxLength: 255 }}
              helperText={`${(slideshowData.buttonLabel || "").length}/255`}
            />

            <Box sx={{ mt: 4, mb: 2 }}>
              <Typography variant="h6">Desktop Image</Typography>
              <Attachments
                showMainImage={true}
                showReport={false}
                onAttachmentsChange={(attachments) =>
                  handleAttachmentsChange(attachments, "desktopImage")
                }
                media={
                  media
                    ? {
                        mainImage: media.desktopImage,
                      }
                    : null
                }
                onDeleteMainImage={() => {
                  setSlideshowData((prev) => ({
                    ...prev,
                    deleteDesktopImage: true,
                  }));
                }}
              />
            </Box>

            <Box sx={{ mt: 4, mb: 2 }}>
              <Typography variant="h6">Mobile Image</Typography>
              <Attachments
                showMainImage={true}
                showReport={false}
                onAttachmentsChange={(attachments) =>
                  handleAttachmentsChange(attachments, "mobileImage")
                }
                media={
                  media
                    ? {
                        mainImage: media.mobileImage,
                      }
                    : null
                }
                onDeleteMainImage={() => {
                  setSlideshowData((prev) => ({
                    ...prev,
                    deleteMobileImage: true,
                  }));
                }}
              />
            </Box>

            <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => setIsAddingItem(false)}
                sx={{ marginRight: 2 }}
                color="error"
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveItem}
                color="primary"
                variant="contained"
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
      <Banner
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        handleClose={() => setSnackbarOpen(false)}
      />
    </StyledContainer>
  );
}
